import {
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from 'react';
import { Search } from 'lucide-react';
import { cn } from '../lib/cn.js';
import { hueOf } from './avatar-color.js';

/**
 * The primitives this app actually uses.
 *
 * PLAN.md §6 is explicit that `packages/ui` must NOT be built speculatively:
 * "extract a component only once the same pattern appears three times in Phase
 * 3. A design system built before it has consumers is a reliable time sink."
 *
 * So these live here, in the app, until that threshold is met. Radix supplies
 * the primitives with real behaviour — focus traps, roving tabindex, escape
 * handling — and these are the styled shells around them plus the handful of
 * things Radix has no opinion about.
 */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
type ButtonSize = 'sm' | 'md';

/**
 * `primary` carries the only elevation on a Button — a permanent `shadow-sm`
 * (resolving through `styles.css`'s `@theme` override of Tailwind's own
 * shadow tokens, not a bespoke value) — because it is the one variant making
 * a claim to be THE action on its surface; giving every variant a shadow
 * would just make the page noisier, not clearer. A bigger shadow on HOVER
 * was tried and cut: `--shadow-lg` is tuned for Popover/Modal-sized panels,
 * and jumping to it on a `h-9` button read as the button floating rather
 * than lifting. The hover/press feedback stays where it already read
 * correctly — `bg-accent-hover` and the `press` class's active-state scale
 * (`styles.css`). `secondary` and `danger` stay flat, matching their
 * border-defined shape; `ghost` never had a shape to raise in the first
 * place.
 */
const BUTTON_VARIANTS: Readonly<Record<ButtonVariant, string>> = {
  /* The inset white ring is the machined edge real SaaS buttons have — a
     flat colour block on a dark surface reads as a sticker, and a hairline
     highlight along the top edge is what makes it read as a physical
     control. On the accent fill it is nearly invisible, which is the point:
     it is a boundary, not a decoration. */
  primary:
    'bg-accent text-accent-ink shadow-[0_2px_8px_color-mix(in_oklab,var(--color-accent)_30%,transparent)] ring-1 ring-inset ring-white/10 hover:bg-accent-hover hover:shadow-[0_4px_12px_color-mix(in_oklab,var(--color-accent)_40%,transparent)]',
  secondary:
    'bg-surface-raised text-ink border border-line/60 shadow-sm hover:bg-surface-hover hover:border-line-strong',
  ghost: 'text-ink-muted hover:bg-surface-hover hover:text-ink',
  danger:
    'bg-danger text-danger-ink shadow-sm ring-1 ring-inset ring-white/10 hover:bg-danger/90 hover:shadow-[0_2px_8px_color-mix(in_oklab,var(--color-danger)_30%,transparent)]',
};

const BUTTON_SIZES: Readonly<Record<ButtonSize, string>> = {
  sm: 'h-7 px-2 text-xs gap-1',
  md: 'h-9 px-3 text-sm gap-1.5',
};

export interface ButtonProps extends ComponentPropsWithoutRef<'button'> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  className,
  type,
  ...props
}: ButtonProps) {
  return (
    <button
      /* Explicit, because the HTML default is `submit`. A styled button inside a
         form that was only meant to open a menu otherwise submits the form —
         which in this app means saving a half-edited card. */
      type={type ?? 'button'}
      className={cn(
        'press inline-flex items-center justify-center rounded font-medium transition-[color,box-shadow,border-color,background-color] duration-150 ease-out',
        'disabled:pointer-events-none disabled:opacity-50',
        BUTTON_VARIANTS[variant],
        BUTTON_SIZES[size],
        className,
      )}
      {...props}
    />
  );
}

export type IconButtonSize = 'sm' | 'md';

/**
 * A plain, borderless icon-only button — the shell's own hamburger and
 * keyboard-shortcuts triggers, and chat's header icons, had each written
 * the identical `flex size-8 shrink-0 items-center justify-center
 * rounded-lg ...` class string by hand. `Button`'s own sizes carry
 * horizontal padding built for text and are the wrong shape for an
 * icon-only square, which is why this is a second, small primitive.
 */
export function IconButton({
  size = 'md',
  active = false,
  className,
  type,
  ...props
}: ComponentPropsWithoutRef<'button'> & {
  readonly size?: IconButtonSize;
  readonly active?: boolean;
}) {
  return (
    <button
      type={type ?? 'button'}
      className={cn(
        'press flex shrink-0 items-center justify-center rounded-lg transition-colors duration-150 ease-out',
        size === 'sm' ? 'size-7' : 'size-8',
        active
          ? 'bg-accent/10 text-accent'
          : 'text-ink-muted hover:bg-surface-hover hover:text-ink',
        className,
      )}
      {...props}
    />
  );
}

/* `ComponentProps`, not `ComponentPropsWithoutRef`: React 19 passes `ref` as an
   ordinary prop, and `FocusOnMountInput` below needs to forward one. */
export type InputProps = ComponentProps<'input'>;

export function Input({ className, ...props }: InputProps) {
  return (
    <input
      className={cn(
        'h-9 w-full rounded-lg border border-line/50 bg-surface-sunken px-3 text-sm text-ink transition-all shadow-[inset_0_1px_2px_oklch(0%_0_0/6%)]',
        'placeholder:text-ink-faint focus:border-accent focus:bg-surface focus:ring-2 focus:ring-accent/25 focus:shadow-[0_0_12px_color-mix(in_oklab,var(--color-accent)_20%,transparent)] focus:outline-none',
        'disabled:opacity-50',
        className,
      )}
      {...props}
    />
  );
}

export function SearchInput({
  value,
  onChange,
  placeholder,
  className,
  'aria-label': ariaLabel,
}: {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder?: string;
  readonly className?: string;
  readonly 'aria-label'?: string;
}) {
  return (
    <div className={cn('relative h-9', className)}>
      <Search
        aria-hidden="true"
        className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-ink-faint"
        strokeWidth={2}
      />
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        aria-label={ariaLabel ?? placeholder}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className="h-9 w-full rounded-lg border border-line/50 bg-surface-sunken py-2 pr-3 pl-8 text-sm text-ink transition-all placeholder:text-ink-faint focus:border-accent focus:bg-surface focus:ring-2 focus:ring-accent/25 focus:shadow-[0_0_12px_color-mix(in_oklab,var(--color-accent)_20%,transparent)] focus:outline-none"
      />
    </div>
  );
}

/**
 * An input that takes focus when it appears.
 *
 * Not `autoFocus`. That prop moves focus on MOUNT regardless of why the element
 * appeared — on a page load it relocates a screen reader user without their
 * asking, which is why `jsx-a11y/no-autofocus` flags it. Focusing in an effect
 * is the same DOM call made deliberately, and it is only correct where the
 * element appeared BECAUSE of something the user just did: the inline-edit input
 * in the table view, which replaces the cell they double-clicked. If the control
 * would be there anyway, it should not steal focus at all.
 */
export function FocusOnMountInput({ className, ...props }: InputProps) {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    ref.current?.select();
  }, []);

  return <Input ref={ref} className={className} {...props} />;
}

export type TextareaProps = ComponentPropsWithoutRef<'textarea'>;

export function Textarea({ className, ...props }: TextareaProps) {
  return (
    <textarea
      className={cn(
        'w-full rounded-lg border border-line/50 bg-surface-sunken px-3 py-2 text-sm text-ink transition-all',
        'placeholder:text-ink-faint focus:border-accent focus:bg-surface focus:ring-2 focus:ring-accent/25 focus:shadow-[0_0_12px_color-mix(in_oklab,var(--color-accent)_20%,transparent)] focus:outline-none',
        className,
      )}
      {...props}
    />
  );
}

export interface FieldProps {
  readonly label: string;
  readonly htmlFor: string;
  readonly error?: string | undefined;
  readonly hint?: string | undefined;
  readonly children: ReactNode;
}

/**
 * A labelled form control.
 *
 * The error is wired with `aria-describedby` by the caller passing the same id;
 * a validation message that is only red text is invisible to a screen reader,
 * which means the form is unusable rather than merely unattractive.
 *
 * ## Laying these out in a ROW
 *
 * The three parts stack, so a Field carrying a hint or an error is TALLER than
 * one that is not — which makes `items-end` the wrong choice for a horizontal
 * row, even though it is the intuitive one. Aligning bottom edges puts a
 * hintless field's input level with its neighbour's hint text, one line below
 * where it looks like it belongs, and the row only goes crooked once a hint
 * appears.
 *
 * Use `items-start` instead: every label is one line of `text-xs`, so the
 * controls line up on their own and the hints hang off the bottom without
 * moving anything. Bare controls in the same row (a submit button, a checkbox)
 * carry `mt-5` to drop past the label — 1rem of label plus `space-y-1`.
 */
export function Field({ label, htmlFor, error, hint, children }: FieldProps) {
  return (
    <div className="space-y-1">
      <label htmlFor={htmlFor} className="block text-xs font-medium text-ink-muted">
        {label}
      </label>
      {children}
      {hint !== undefined && error === undefined && (
        <p className="text-xs text-ink-faint">{hint}</p>
      )}
      {error !== undefined && (
        <p id={`${htmlFor}-error`} role="alert" className="text-xs text-danger">
          {error}
        </p>
      )}
    </div>
  );
}

export interface BadgeProps {
  readonly children: ReactNode;
  readonly className?: string;
  readonly title?: string;
}

export function Badge({ children, className, title }: BadgeProps) {
  return (
    <span
      title={title}
      className={cn(
        /* `text-xs`, not the 11px this used to be. 11px is below the floor the
           UI/UX redesign pass sets for anything a person reads by scanning —
           badge text was the single most common 11px offender (292 instances
           of 9-11px across the app), and a count or a role label is read far
           more often than it is decorative. The slightly larger footprint is
           the same 12px the board's other metadata uses, so badges sit level
           with their neighbours rather than one size down from them. */
        'inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs font-medium',
        'bg-surface-hover/80 text-ink-muted border border-line/30',
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * Initials for a label that is usually an email address.
 *
 * `tenancy.members.list` returns `email` and no display name (there is no
 * profile surface yet), so the local part is all there is to work with.
 * `ada.lovelace@x.com` gives AL, `ada@x.com` gives AD — two characters either
 * way, because a stack of avatars that are sometimes one character wide and
 * sometimes two does not line up.
 */
function initialsOf(label: string): string {
  const local = (label.split('@')[0] ?? label).trim();
  const parts = local.split(/[._\-+\s]+/u).filter((part) => part.length > 0);

  const first = parts[0] ?? label;
  const second = parts[1];

  const initials =
    second === undefined ? first.slice(0, 2) : `${first.slice(0, 1)}${second.slice(0, 1)}`;

  return initials.toUpperCase() || '?';
}

export interface AvatarProps {
  readonly userId: string;
  /** Email or display name. Shown on hover and to assistive tech. */
  readonly label: string;
  readonly size?: 'xs' | 'sm';
  readonly className?: string;
}

/**
 * A person, as a coloured disc.
 *
 * `role="img"` with an `aria-label` rather than a bare styled span: the initials
 * inside are a rendering of the label, not text worth reading out, and without
 * the role a screen reader announces "AL" — which identifies nobody.
 */
export function Avatar({ userId, label, size = 'sm', className }: AvatarProps) {
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      style={{ backgroundColor: `oklch(58% 0.13 ${String(hueOf(userId))})` }}
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-medium text-white',
        'ring-1 ring-surface-raised',
        /* 10/11px initials, not the 9/10px this used to be — two characters in
           9px on a 20px disc are a smudge, and initials are the identifier a
           stack of avatars is scanned by. */
        size === 'xs' ? 'size-5 text-[10px]' : 'size-6 text-[11px]',
        className,
      )}
    >
      {initialsOf(label)}
    </span>
  );
}

/**
 * Overlapping avatars with an overflow count.
 *
 * The overflow badge carries the remaining NAMES in its title, because "+3" that
 * cannot be resolved to people is a decoration. Capped rather than scrollable —
 * a card tile is not the place to enumerate a team.
 */
export function AvatarStack({
  people,
  max = 3,
  size = 'xs',
}: {
  readonly people: readonly { userId: string; label: string }[];
  readonly max?: number;
  readonly size?: 'xs' | 'sm';
}) {
  if (people.length === 0) return null;

  const shown = people.slice(0, max);
  const hidden = people.slice(max);

  return (
    <span className="flex items-center -space-x-1">
      {shown.map((person) => (
        <Avatar key={person.userId} userId={person.userId} label={person.label} size={size} />
      ))}
      {hidden.length > 0 && (
        <span
          title={hidden.map((person) => person.label).join(', ')}
          className={cn(
            'inline-flex shrink-0 items-center justify-center rounded-full',
            'bg-surface-hover text-ink-muted ring-1 ring-surface-raised',
            size === 'xs' ? 'size-5 text-[10px]' : 'size-6 text-[11px]',
          )}
        >
          +{hidden.length}
        </span>
      )}
    </span>
  );
}

export function Spinner({ className }: { readonly className?: string }) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cn(
        'inline-block size-4 animate-spin rounded-full border-2 border-line border-t-accent',
        className,
      )}
    />
  );
}

/**
 * A loading placeholder shaped like the thing that is loading.
 *
 * Preferred over `Spinner` for any region whose shape is known in advance, which
 * is most of this app: a board has columns, a table has rows, a card panel has
 * sections. A centred spinner on an otherwise empty page communicates "busy"
 * and nothing else, and at the moment it disappears the layout jumps into
 * existence — so every load reads as a reflow rather than as content arriving.
 *
 * `Spinner` stays for the cases where the shape genuinely is not known: a button
 * mid-submit, and the boot gate before the app knows what it is rendering.
 *
 * `aria-hidden` and no `role="status"` on purpose. The REGION announces its own
 * busy state via `aria-busy` on the container; a skeleton that also announced
 * itself would read out one "Loading" per grey rectangle, which is how a
 * fourteen-row table becomes fourteen announcements.
 */
export function Skeleton({ className }: { readonly className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn('block animate-pulse rounded bg-surface-hover', className)}
    />
  );
}

/** Stacked skeleton lines, for a list whose row height is known. */
export function SkeletonRows({
  rows = 5,
  className,
}: {
  readonly rows?: number;
  readonly className?: string;
}) {
  return (
    <div aria-busy="true" className={cn('space-y-2', className)}>
      {Array.from({ length: rows }, (_, index) => (
        <Skeleton key={index} className="h-9 w-full" />
      ))}
    </div>
  );
}

/**
 * The empty state for a list that loaded successfully and has nothing in it.
 *
 * Distinct from an error and from a spinner on purpose. Rendering nothing for
 * all three makes "you have no projects", "the request failed", and "still
 * loading" indistinguishable, and the user's next action differs in each case.
 *
 * The redesign pass's version: an optional icon in a ringed disc (the same
 * container the app uses for its other lone-glyph moments), a larger
 * max-width'd description, and a touch more vertical room. An empty state is
 * where a product has the most freedom to look designed, because nothing is
 * competing with it — a bare dashed box reads as "unfinished", which is the
 * exact impression a demo with no data leaves.
 */
export function Empty({
  title,
  description,
  action,
  icon,
}: {
  readonly title: string;
  readonly description?: string;
  readonly action?: ReactNode;
  /** A lone glyph above the title — the same "icon in a disc" shape the app uses elsewhere. */
  readonly icon?: ReactNode;
}) {
  return (
    <div className="empty-fade flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-line/50 bg-surface-sunken/30 p-12 text-center">
      {icon !== undefined && (
        <span className="mb-1 flex size-12 items-center justify-center rounded-full bg-surface-raised text-ink-faint ring-1 ring-line/50">
          {icon}
        </span>
      )}
      <p className="text-[15px] font-semibold text-ink">{title}</p>
      {description !== undefined && (
        <p className="max-w-sm text-[13px] leading-relaxed text-ink-muted">{description}</p>
      )}
      {action !== undefined && <div className="mt-1">{action}</div>}
    </div>
  );
}

/* -------------------------------------------------------------------------- *
 * Settings layout
 *
 * These three arrived by the §6 rule rather than ahead of it: `Section` is used
 * eight times across the org and project settings pages, `AddPanel` four, and
 * `ConfirmButton` five. Each existed first as copied markup in one file, and was
 * lifted only once a second file needed the same thing.
 * -------------------------------------------------------------------------- */

/**
 * The page header — one pattern for every surface in the app.
 *
 * Before this, every page hand-rolled its own `h1 + p` block and they had
 * drifted into four different sizes and rhythms (`text-base`, `text-lg`, one
 * at `text-xl`), which is exactly how an app reads as "assembled" rather than
 * designed. One component, `font-display` (Geist) at `text-xl` like the login
 * page's own heading, description at `text-sm`, actions pinned right — the
 * same hierarchy Linear/ClickUp/Twilio use on every one of their pages.
 */
export function PageHeader({
  title,
  description,
  actions,
}: {
  readonly title: string;
  readonly description?: string | undefined;
  readonly actions?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div className="min-w-0">
        <h1 className="font-display text-2xl font-bold tracking-tight text-ink">{title}</h1>
        {description !== undefined && (
          <p className="mt-1.5 max-w-2xl text-[15px] leading-relaxed text-ink-muted">
            {description}
          </p>
        )}
      </div>
      {actions !== undefined && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

/**
 * A titled block, with an optional count and description.
 *
 * Exists because the heading markup was duplicated eight times and had already
 * drifted: two spacing rhythms, and sections that silently lacked a description.
 *
 * The redesign pass moved the title off the ALL-CAPS micro-label (`text-xs
 * uppercase`) onto a sentence-case 13px heading. Uppercase micro-labels are
 * the visual signature of a default admin panel — real SaaS settings surfaces
 * (Linear, Twilio console, Stripe) set section titles in the same size and
 * weight as the content around them, one rung below the page title, and let
 * the type hierarchy carry the grouping instead of the tracking.
 */
export function Section({
  title,
  count,
  description,
  children,
}: {
  readonly title: string;
  /* `| undefined` explicitly, not just `?`. Under `exactOptionalPropertyTypes`
     an optional prop rejects an explicitly-passed `undefined`, and callers pass
     `data?.length` — which is exactly that while the query is loading. */
  readonly count?: number | undefined;
  readonly description?: string | undefined;
  readonly children: ReactNode;
}) {
  return (
    <section className="space-y-4">
      <div className="flex items-center gap-2">
        <h2 className="text-base font-semibold text-ink">{title}</h2>
        {count !== undefined && <Badge>{count}</Badge>}
      </div>
      {description !== undefined && (
        <p className="text-[13px] leading-relaxed text-ink-muted">{description}</p>
      )}
      {children}
    </section>
  );
}

/**
 * The bordered block an "add" control sits in.
 *
 * Dashed and sunken so the top of a list reads as a control rather than as the
 * collection's first item — which is the risk of putting the form above the
 * list, and the reason it is worth putting there anyway: a form BELOW forty rows
 * moves further away the more the page is used.
 */
export function AddPanel({ children }: { readonly children: ReactNode }) {
  return (
    <div className="rounded-xl border border-dashed border-line/50 bg-surface-sunken/40 p-4">
      {children}
    </div>
  );
}

/**
 * A destructive action that takes two clicks.
 *
 * Not `window.confirm`: that dialog is unstyleable, blocks the whole tab, and
 * users dismiss it reflexively. Revealing the confirmation in place means the
 * second click is on a different, differently-labelled control than the first,
 * and clicking anywhere else cancels rather than confirming.
 *
 * `confirmLabel` is separate from `label` so the caller can state the BLAST
 * RADIUS at the moment it matters — "Delete from 12 cards" is a different
 * decision from "Delete", and the count is only known per row.
 *
 * Reserved for genuinely irreversible actions. Archiving is NOT one: it has
 * Restore beside it, and a confirm on something already undoable is the noise
 * that trains people to click through the confirms that matter.
 */
export function ConfirmButton({
  label,
  confirmLabel,
  onConfirm,
  disabled = false,
  size = 'sm',
  className,
}: {
  readonly label: string;
  readonly confirmLabel?: string;
  readonly onConfirm: () => void;
  readonly disabled?: boolean;
  readonly size?: ButtonSize;
  readonly className?: string;
}) {
  const [confirming, setConfirming] = useState(false);

  if (!confirming) {
    return (
      <Button
        size={size}
        variant="ghost"
        className={cn('text-ink-faint hover:text-danger', className)}
        disabled={disabled}
        onClick={() => {
          setConfirming(true);
        }}
      >
        {label}
      </Button>
    );
  }

  /* `className` reaches BOTH states, not just the resting one. It used to be
     applied only to the pre-confirm button, so a caller that sized the control
     to fit a dense row got its sizing dropped the instant it was clicked — the
     row grew, and everything below it moved while the person was aiming at
     "Confirm". A confirmation that shifts its own target under the pointer is
     the one moment a size change costs the most. */
  return (
    <span className="flex items-center gap-1">
      <Button
        size={size}
        variant="ghost"
        className={cn('text-danger', className)}
        disabled={disabled}
        onClick={() => {
          onConfirm();
          setConfirming(false);
        }}
      >
        {confirmLabel ?? `Confirm ${label.toLowerCase()}`}
      </Button>
      <Button
        size={size}
        variant="ghost"
        className={className}
        onClick={() => {
          setConfirming(false);
        }}
      >
        Cancel
      </Button>
    </span>
  );
}

/**
 * A `role="tablist"` bar for a search-param-driven section switcher — the
 * shape `platform-admin-page.tsx`, `analytics-page.tsx`, and
 * `import-export-dialog.tsx` had each hand-rolled independently, found while
 * auditing the warm-dark rebuild's own component-consolidation pass
 * (`ai/design-rebuild-warm-dark.md` §3): the same `role="tablist"`/
 * `role="tab"`/`aria-selected` shape, the same active/inactive class pair,
 * each with its own small drift from the other two (a plain template-literal
 * className instead of `cn()`, a solid `bg-accent` active background instead
 * of the ring style below, no shared way to stretch tabs to equal width).
 * Lives here rather than `platform-admin/shared.tsx` (where the first,
 * two-caller version of this started) because every one of its real callers
 * now spans unrelated features — this is the shared-across-features layer.
 *
 * Generic over the value type so a nullable "All" filter and a plain
 * non-null string union share one implementation instead of one being a
 * near-copy of the other with a `?? 'all'` key fallback bolted on.
 *
 * `icon` is a rendered node, not a component reference — the same choice
 * `Empty`'s own `icon` prop makes just above, so this file never needs to
 * import `lucide-react` itself; the caller renders its own icon element
 * exactly as it would inline.
 *
 * `stretch` makes every tab share the row's width equally
 * (`import-export-dialog.tsx`'s two-item export/import switch, whose narrow
 * dialog width makes an intrinsic-width row look wrong) — a real, deliberate
 * visual change for that one adopter (solid active background → this
 * component's own ring-based one), not an accidental behavior change.
 */
export function TabBar<T extends string | null>({
  items,
  value,
  onChange,
  ariaLabel,
  size = 'sm',
  stretch = false,
  className,
}: {
  readonly items: readonly {
    readonly value: T;
    readonly label: string;
    readonly icon?: ReactNode;
  }[];
  readonly value: T;
  readonly onChange: (value: T) => void;
  readonly ariaLabel: string;
  readonly size?: 'sm' | 'xs';
  readonly stretch?: boolean;
  readonly className?: string;
}) {
  return (
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'inline-flex gap-0.5 rounded-xl border border-line bg-surface-sunken/80 p-1 overflow-x-auto',
        className,
      )}
    >
      {items.map(({ value: itemValue, label, icon }) => (
        <button
          key={itemValue ?? 'null'}
          type="button"
          role="tab"
          aria-selected={value === itemValue}
          onClick={() => {
            onChange(itemValue);
          }}
          className={cn(
            'relative flex items-center justify-center gap-1.5 rounded-lg px-3 py-1.5 font-medium whitespace-nowrap transition-all duration-150',
            size === 'sm' ? 'text-sm' : 'text-xs',
            stretch && 'flex-1',
            value === itemValue
              ? 'bg-accent/10 text-accent shadow-sm ring-1 ring-accent/20'
              : 'text-ink-muted hover:bg-surface-hover hover:text-ink',
          )}
        >
          {icon}
          {label}
        </button>
      ))}
    </div>
  );
}

/**
 * A `<nav>` + `aria-current="page"` section switcher — deliberately NOT
 * `TabBar` (`role="tablist"`/`role="tab"`/`aria-selected`) above, even
 * though every one of its real callers looks identical to a TabBar caller
 * at a glance. The ARIA distinction is real: a `tablist` describes several
 * panels of one widget, none individually a navigable destination; every
 * caller of THIS component (`automations-page.tsx`, `telephony-page.tsx`)
 * is a search-param-driven view with its own shareable, back-button-correct
 * URL — a `nav` of page-like links is the correct semantic for that, the
 * same reasoning those two files' own header comments already state for
 * choosing `aria-current` over `aria-selected` in the first place. Found
 * duplicated byte-for-byte between the two files during the warm-dark
 * rebuild's own component-consolidation pass
 * (`ai/design-rebuild-warm-dark.md` §3): the identical button classes,
 * the identical `bg-accent text-accent-ink shadow-sm` active state, only
 * automations-page.tsx's optional per-tab count badge differing.
 */
export function NavTabs<T extends string>({
  items,
  value,
  onChange,
  ariaLabel,
  className,
}: {
  readonly items: readonly {
    readonly value: T;
    readonly label: string;
    /** A small count pill after the label — automations-page.tsx's own
     * per-tab "how many" indicator. Omitted entirely (not just hidden)
     * while still loading, so a zero that means "no data yet" is never
     * shown as if it meant "confirmed empty". */
    readonly badge?: string | number;
  }[];
  readonly value: T;
  readonly onChange: (value: T) => void;
  readonly ariaLabel: string;
  readonly className?: string;
}) {
  return (
    <nav aria-label={ariaLabel} className={cn('flex gap-1', className)}>
      {items.map(({ value: itemValue, label, badge }) => (
        <button
          key={itemValue}
          type="button"
          aria-current={value === itemValue ? 'page' : undefined}
          onClick={() => {
            onChange(itemValue);
          }}
          className={cn(
            'flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
            value === itemValue
              ? 'bg-accent text-accent-ink shadow-sm'
              : 'text-ink-muted hover:bg-surface-hover hover:text-ink',
          )}
        >
          {label}
          {badge !== undefined && (
            <span
              className={cn(
                'rounded px-1 text-[10px] tabular-nums',
                value === itemValue ? 'bg-accent-ink/20' : 'bg-surface-sunken text-ink-faint',
              )}
            >
              {badge}
            </span>
          )}
        </button>
      ))}
    </nav>
  );
}
